/**

 * Apply editor draft / layout-table proxy onto selectedSite for Site 3D preview.

 *

 * Site 3D reads layout* the same way as fetchUserSite (group-local bin centers,

 * group-local absolute side endpoints). Use layoutTableProxy.js — not a second

 * coordinate path.

 */

import * as global from "../data/global";

import {

    applyLayoutTableProxyToSite,

    attachPreviewPolygonsFromLayoutSides,

    buildDraftFromFetchedLayoutTables,

    buildLayoutTableProxyFromEditorDraft,

    loadLayoutDraftForSite3d,

    loadLayoutProxyForSite3d,
    resolveLayoutProxyForSite3d,
    resolveServerLayoutProxyForSite3d,
    extractLayoutProxyFromEditorDraft,
    extractLayoutProxyFromSiteFetch,

    logLayoutProxyRoundTripDrift,

    promoteLayoutSidesForDrawSite,

    repositionLayoutSiteForSite3d,

    sameBinDataId,

    layoutBinMatchKey,

    layoutBinMatchKeys,

    findSiteBinForLayoutBin,

    isLayoutPlaceholderBinId,

    normalizeLayoutBinLabel,

} from "./layoutTableProxy.js";

const findTargetLayoutBin = (srcBin, targetByKey) => {
    if (!srcBin || !targetByKey) return null;
    for (const key of layoutBinMatchKeys(srcBin)) {
        const tgt = targetByKey.get(key);
        if (tgt) return tgt;
    }
    return null;
};

/** Round-bin width/length from editor draft (canvas footprint), not BTS catalog diameter. */
const mergeRoundBinDimensionsFromDraft = (site, draft) => {
    if (!site?.layoutBins?.length || !draft?.groups?.length) return;
    const draftByKey = new Map();
    draft.groups.forEach((g) => {
        (g.bins || []).forEach((b) => {
            if (!b.isRound) return;
            const key = layoutBinMatchKey({ binId: b.binId, binName: b.binName });
            if (key) draftByKey.set(key, b);
        });
    });
    site.layoutBins.forEach((lb) => {
        if (!lb.isRound) return;
        const src = draftByKey.get(layoutBinMatchKey(lb));
        if (!src || !(src.width > 0)) return;
        lb.width = src.width;
        lb.length = src.length > 0 ? src.length : src.width;
        if (lb.bin) {
            lb.bin.width = lb.width;
            lb.bin.length = lb.length;
            if (lb.bin.diameter != null) lb.bin.diameter = lb.width;
        }
    });
};

export {
    sameBinDataId,
    buildLayoutTableProxyFromEditorDraft,
    applyLayoutTableProxyToSite,
    finalizeLayoutSidesForDrawSite,
    promoteLayoutSidesForDrawSite,
} from "./layoutTableProxy.js";



export const ensureLayoutSideRenderGeometry = (site) =>

    global.ensureLayoutSideRenderGeometry(site);



/** @deprecated Use layoutTableProxy sideToGroupAbsolute */

export const sticeSidesToWorldCoords = (sides, binX, binZ) => {

    const bx = binX || 0;

    const bz = binZ || 0;

    return (sides || []).map((s) => ({

        ...s,

        x1: bx + (s.x1 ?? 0),

        y1: s.y1 ?? 0,

        z1: bz + (s.z1 ?? 0),

        x2: bx + (s.x2 ?? 0),

        y2: s.y2 ?? 0,

        z2: bz + (s.z2 ?? 0),

        curveX: bx + (s.curveX ?? 0),

        curveY: s.curveY ?? 0,

        curveZ: bz + (s.curveZ ?? 0),

    }));

};



export const resetSiteLayoutRefs = (site) => {

    if (!site) return;

    site.siteRef = [];

    site.groupsRef = [];

    site.binsRef = [];

    site.sensorsGroupsRef = [];

};



export const snapshotSiteLayout = (site) => {

    if (!site) return null;

    return {

        layout: site.layout,

        layoutGroups: site.layoutGroups,

        layoutBins: site.layoutBins,

        layoutSides: site.layoutSides,

        layoutCables: site.layoutCables,

        layoutSensors: site.layoutSensors,

        isGradesOn: site.isGradesOn,

        siteLayoutTableProxy: site.siteLayoutTableProxy ?? null,

    };

};



export const restoreSiteLayout = (site, snap) => {

    if (!site || !snap) return;

    site.layout = snap.layout;

    site.layoutGroups = snap.layoutGroups;

    site.layoutBins = snap.layoutBins;

    site.layoutSides = snap.layoutSides;

    site.layoutCables = snap.layoutCables;

    site.layoutSensors = snap.layoutSensors;

    site.siteLayoutTableProxy = snap.siteLayoutTableProxy ?? null;

    if (snap.isGradesOn !== undefined) {

        site.isGradesOn = snap.isGradesOn;

    }

    resetSiteLayoutRefs(site);

};



/**

 * Editor draft → layout table proxy → selectedSite (preview / pre-save validation).

 */

export const applyLayoutDraftToSite = (site, draft, options = {}) => {

    if (!site || !draft) return false;

    const proxy = buildLayoutTableProxyFromEditorDraft(draft, site.siteGuid);

    if (!proxy) return false;

    site.siteLayoutTableProxy = proxy;

    return applyLayoutTableProxyToSite(site, proxy, options);

};



/** Layout bases only (no cables). */
export const applyLayoutDraftToSiteForPreview = (site, draft) =>
    applyLayoutDraftToSite(site, draft, { basesOnly: true });

/** Preview 3D: draft layout positions; cables/sensors applied separately from production rows. */
export const applyLayoutDraftToSiteForPreview3d = (site, draft) =>
    applyLayoutDraftToSite(site, draft, {
        basesOnly: false,
        skipCablePopulate: true,
        skipDrawSite: true,
    });

/**
 * Keep persisted CustomerSiteLayoutCable/Sensor positions when previewing a draft.
 * BTS cableList coords are often tighter than saved layout rows — remaps ids onto draft bins.
 */
export const remapLayoutCablesToBins = (
    sourceCables,
    sourceLayoutBins,
    targetLayoutBins
) => {
    if (!sourceCables?.length || !targetLayoutBins?.length) return [];

    const targetByKey = new Map();
    targetLayoutBins.forEach((lb) => {
        layoutBinMatchKeys(lb).forEach((key) => targetByKey.set(key, lb));
    });

    const sourceBinById = new Map(
        (sourceLayoutBins || []).map((lb) => [lb.customerSiteLayoutBinId, lb])
    );

    let nextCableId = 1;
    const remapped = [];
    sourceCables.forEach((lc) => {
        const srcBin = sourceBinById.get(lc.customerSiteLayoutBinId);
        if (!srcBin) return;
        const tgt = findTargetLayoutBin(srcBin, targetByKey);
        if (!tgt) return;
        remapped.push({
            ...lc,
            customerSiteLayoutCableId: nextCableId++,
            customerSiteLayoutId: tgt.customerSiteLayoutId ?? lc.customerSiteLayoutId ?? 1,
            customerSiteLayoutGroupId: tgt.customerSiteLayoutGroupId,
            customerSiteLayoutBinId: tgt.customerSiteLayoutBinId,
            binId: tgt.binId,
            binName: tgt.binName ?? lc.binName,
        });
    });
    return remapped;
};

export const remapLayoutSensorsToCables = (
    sourceSensors,
    sourceCables,
    remappedCables
) => {
    if (!sourceSensors?.length || !remappedCables?.length) return [];

    const oldCableById = new Map(
        (sourceCables || []).map((c) => [c.customerSiteLayoutCableId, c])
    );
    const newCableKey = new Map();
    remappedCables.forEach((c) => {
        const key = `${c.binId ?? ""}:${c.cableId ?? ""}:${c.cableName ?? ""}`;
        newCableKey.set(key, c);
    });

    let nextSensorId = 1;
    const out = [];
    sourceSensors.forEach((ls) => {
        const oldCable = oldCableById.get(ls.customerSiteLayoutCableId);
        if (!oldCable) return;
        const key = `${oldCable.binId ?? ""}:${oldCable.cableId ?? ""}:${oldCable.cableName ?? ""}`;
        const newCable = newCableKey.get(key);
        if (!newCable) return;
        out.push({
            ...ls,
            customerSiteLayoutSensorId: nextSensorId++,
            customerSiteLayoutCableId: newCable.customerSiteLayoutCableId,
            customerSiteLayoutId: newCable.customerSiteLayoutId,
            customerSiteLayoutGroupId: newCable.customerSiteLayoutGroupId,
            customerSiteLayoutBinId: newCable.customerSiteLayoutBinId,
            binId: newCable.binId,
            cableId: newCable.cableId,
        });
    });
    return out;
};

/** Uniform scale for round-bin layout cables when preview bin size ≠ production layout. */
const roundBinDiameterFt = (lb) => {
    if (!lb) return 0;
    const w = Number(lb.width);
    const l = Number(lb.length);
    if (lb.isRound) return w > 0 ? w : l > 0 ? l : 0;
    return 0;
};

const LAYOUT_CABLE_POSITION_KEYS = [
    "x",
    "z",
    "topPointX",
    "topPointZ",
    "bottomPointX",
    "bottomPointZ",
    "curveX",
    "curveZ",
];

const scaleLayoutCableToBin = (cable, scaleX, scaleZ) => {
    const sx = scaleX > 0 ? scaleX : 1;
    const sz = scaleZ > 0 ? scaleZ : 1;
    if (Math.abs(sx - 1) < 0.005 && Math.abs(sz - 1) < 0.005) return cable;
    const scaled = { ...cable };
    LAYOUT_CABLE_POSITION_KEYS.forEach((k) => {
        if (!Number.isFinite(scaled[k])) return;
        scaled[k] *= k === "x" || k.includes("X") ? sx : sz;
    });
    if (Number.isFinite(scaled.width)) scaled.width *= sx;
    if (Number.isFinite(scaled.length)) scaled.length *= sz;
    return scaled;
};

/** Shift cable world coords when layout bins moved (fetch vs editor proxy drift). */
const translateLayoutCableByDelta = (cable, dx, dz) => {
    if (Math.abs(dx) < 0.005 && Math.abs(dz) < 0.005) return cable;
    const out = { ...cable };
    LAYOUT_CABLE_POSITION_KEYS.forEach((k) => {
        if (!Number.isFinite(out[k])) return;
        out[k] += k === "x" || k.includes("X") ? dx : dz;
    });
    return out;
};

const translateRemappedCablesByBinDelta = (
    remappedCables,
    sourceLayoutBins,
    targetLayoutBins
) => {
    if (!remappedCables?.length || !targetLayoutBins?.length) {
        return remappedCables ?? [];
    }

    const sourceByKey = new Map();
    (sourceLayoutBins || []).forEach((lb) => {
        layoutBinMatchKeys(lb).forEach((key) => sourceByKey.set(key, lb));
    });

    const targetById = new Map(
        targetLayoutBins.map((lb) => [lb.customerSiteLayoutBinId, lb])
    );

    return remappedCables.map((lc) => {
        const tgt = targetById.get(lc.customerSiteLayoutBinId);
        if (!tgt) return lc;
        const src = findTargetLayoutBin(tgt, sourceByKey);
        if (!src) return lc;
        const dx = (tgt.x ?? 0) - (src.x ?? 0);
        const dz = (tgt.z ?? 0) - (src.z ?? 0);
        return translateLayoutCableByDelta(lc, dx, dz);
    });
};

const scaleRemappedCablesToDraftBins = (
    remappedCables,
    draftBins,
    sourceLayoutBins
) => {
    if (!remappedCables?.length || !draftBins?.length) return remappedCables ?? [];

    const sourceByKey = new Map();
    (sourceLayoutBins || []).forEach((lb) => {
        layoutBinMatchKeys(lb).forEach((key) => sourceByKey.set(key, lb));
    });

    return remappedCables.map((lc) => {
        const draftLb = draftBins.find(
            (b) => b.customerSiteLayoutBinId === lc.customerSiteLayoutBinId
        );
        if (!draftLb) return lc;
        const srcLb = findTargetLayoutBin(draftLb, sourceByKey);
        if (!srcLb) return lc;

        if (draftLb.isRound && srcLb.isRound) {
            const dDraft = roundBinDiameterFt(draftLb);
            const dSrc = roundBinDiameterFt(srcLb);
            const s = dSrc > 0 && dDraft > 0 ? dDraft / dSrc : 1;
            return scaleLayoutCableToBin(lc, s, s);
        }
        const sx =
            srcLb.width > 0 && draftLb.width > 0
                ? draftLb.width / srcLb.width
                : 1;
        const sz =
            srcLb.length > 0 && draftLb.length > 0
                ? draftLb.length / srcLb.length
                : 1;
        return scaleLayoutCableToBin(lc, sx, sz);
    });
};

/** Production layout bin width/length/height (optional; not used for preview canvas match). */
export const mergeLayoutBinDimensionsFromSnapshot = (site, snap) => {
    if (!site?.layoutBins?.length || !snap?.layoutBins?.length) return;
    const snapByKey = new Map();
    snap.layoutBins.forEach((lb) => {
        const key = layoutBinMatchKey(lb);
        if (key) snapByKey.set(key, lb);
    });
    site.layoutBins.forEach((lb) => {
        const src = snapByKey.get(layoutBinMatchKey(lb));
        if (!src) return;
        if (src.width > 0) lb.width = src.width;
        if (src.length > 0) lb.length = src.length;
        if (src.height > 0) lb.height = src.height;
        if (src.rotation != null) lb.rotation = src.rotation;
        if (src.isRound != null) lb.isRound = src.isRound;
        if (lb.bin) {
            if (src.width > 0) {
                lb.bin.width = src.width;
                if (lb.isRound) lb.bin.diameter = src.width;
            }
            if (src.length > 0) lb.bin.length = src.length;
        }
    });
};

const layoutBinsMissingCables = (site) => {
    const covered = new Set(
        (site.layoutCables || []).map((c) => c.customerSiteLayoutBinId)
    );
    return (site.layoutBins || []).filter(
        (lb) => !covered.has(lb.customerSiteLayoutBinId)
    );
};

/** Site-setup bins exist in layout tables but may be absent from a stale LayoutProxyJson snapshot. */
export const mergeLayoutBinsMissingFromSnapshot = (site, snap) => {
    if (!site?.layoutBins || !snap?.layoutBins?.length) return false;

    let changed = false;
    const snapCatalogByLabel = new Map();
    snap.layoutBins.forEach((lb) => {
        if (isLayoutPlaceholderBinId(lb.binId)) return;
        const label = normalizeLayoutBinLabel(lb.binName);
        if (label) snapCatalogByLabel.set(label, lb);
    });

    site.layoutBins = site.layoutBins.map((lb) => {
        if (!isLayoutPlaceholderBinId(lb.binId)) return lb;
        const label = normalizeLayoutBinLabel(lb.binName);
        const catalog = label ? snapCatalogByLabel.get(label) : null;
        if (!catalog) return lb;
        changed = true;
        const siteBin =
            findSiteBinForLayoutBin(site, catalog) ??
            findSiteBinForLayoutBin(site, lb);
        return {
            ...lb,
            binId: catalog.binId,
            binName: catalog.binName ?? lb.binName,
            isRound: catalog.isRound ?? lb.isRound,
            width: lb.width > 0 ? lb.width : catalog.width,
            length: lb.length > 0 ? lb.length : catalog.length,
            height: catalog.height ?? lb.height,
            bin: siteBin
                ? {
                      ...siteBin,
                      width:
                          lb.width > 0
                              ? lb.width
                              : siteBin.width ?? catalog.width,
                      length:
                          lb.length > 0
                              ? lb.length
                              : siteBin.length ?? catalog.length,
                  }
                : lb.bin,
        };
    });

    const haveBinId = new Set(
        site.layoutBins
            .map((lb) => lb.binId)
            .filter((id) => id != null && id > 0)
    );
    const haveLabel = new Set(
        site.layoutBins
            .map((lb) => normalizeLayoutBinLabel(lb.binName))
            .filter(Boolean)
    );

    const merged = [...site.layoutBins];
    let added = false;

    snap.layoutBins.forEach((lb) => {
        if (lb.binId == null || lb.binId <= 0 || haveBinId.has(lb.binId)) return;
        const label = normalizeLayoutBinLabel(lb.binName);
        if (label && haveLabel.has(label)) return;
        haveBinId.add(lb.binId);
        if (label) haveLabel.add(label);
        const siteBin = findSiteBinForLayoutBin(site, lb);
        merged.push({
            ...lb,
            bin:
                siteBin ??
                lb.bin ?? {
                    binDataId: lb.binId,
                    binId: lb.binId,
                    binName: lb.binName,
                    type: lb.isRound ? "Round" : "Flat",
                    height: lb.height || 30,
                    width: lb.width,
                    length: lb.length,
                    cableLayout: "",
                    cableList: [],
                },
        });
        added = true;
    });

    if (added) {
        site.layoutBins = merged;
        changed = true;
    }
    return changed;
};

const renumberRemappedCableBatch = (site, added) => {
    if (!added?.length) return added;
    let nextId = Math.max(
        0,
        ...(site.layoutCables || []).map((c) => c.customerSiteLayoutCableId),
        0
    );
    return added.map((lc) => ({
        ...lc,
        customerSiteLayoutCableId: ++nextId,
    }));
};

/** Site Layout editor / preview: BTS cableList coords are bin-local; parent group uses layout x/z. */
export const siteUsesEditorBinLocalCables = (site, options = {}) =>
    options.rebuildFromBts === true ||
    site?.layoutUsesEditorLayoutAxes ||
    site?.layoutSavedFromSiteLayoutEditor ||
    site?.isLayoutPreview3d;

/**
 * Post-proxy steps shared by Preview 3D overlay and production Site 3D editor layout.
 * Must match previewSite3d.js mountPreviewProxyFromDraft (no reposition / side promote).
 */
export const finalizeEditorLayoutForSite3dView = (site, snap, cableOptions = {}) => {
    if (!site) return;
    attachPreviewPolygonsFromLayoutSides(site);
    try {
        global.ensureLayoutSideRenderGeometry?.(site);
    } catch (err) {
        console.warn("ensureLayoutSideRenderGeometry site3d", err);
    }
    site.layoutUsesEditorLayoutAxes = true;
    site.layoutSavedFromSiteLayoutEditor = true;
    applyPreview3dCablesFromSnapshot(site, snap, {
        rebuildFromBts: true,
        ...cableOptions,
    });
    global.reconcileLayoutCableBinLinks?.(site);
};

/** Rebuild layoutCables from BTS cableList after editor bin moves (no world delta on local coords). */
export const rebuildLayoutCablesFromBtsBins = (site) => {
    if (!site?.bins?.length) return false;
    const hasCables = site.bins.some((b) => b.cableList?.length > 0);
    if (!hasCables) return false;
    global.populateLayoutCablesFromBins(site, { force: true });
    return (site.layoutCables?.length ?? 0) > 0;
};

/** Pull persisted/API cables onto layout bins that still have none after a partial remap. */
export const fillMissingLayoutCablesFromSnapshot = (site, snap) => {
    if (!site || !snap?.layoutCables?.length) return;
    if (siteUsesEditorBinLocalCables(site)) return;
    const missingBins = layoutBinsMissingCables(site);
    if (!missingBins.length) return;

    const sourceBins =
        snap.layoutBins?.length > 0 ? snap.layoutBins : site.layoutBins;
    let added = remapLayoutCablesToBins(
        snap.layoutCables,
        sourceBins,
        missingBins
    );
    if (!added.length) return;

    added = translateRemappedCablesByBinDelta(added, sourceBins, site.layoutBins);
    added = scaleRemappedCablesToDraftBins(added, site.layoutBins, sourceBins);
    added = renumberRemappedCableBatch(site, added);

    const priorCables = site.layoutCables || [];
    site.layoutCables = [...priorCables, ...added];
    const priorSensors = site.layoutSensors || [];
    site.layoutSensors = [
        ...priorSensors,
        ...remapLayoutSensorsToCables(
            snap.layoutSensors,
            snap.layoutCables,
            added
        ),
    ];
};

/** Apply draft layout bins but keep production layout cable/sensor spacing for preview. */
export const applyPreview3dCablesFromSnapshot = (site, snap, options = {}) => {
    if (!site || !snap) return;

    if (siteUsesEditorBinLocalCables(site, options)) {
        rebuildLayoutCablesFromBtsBins(site);
        global.fillMissingLayoutCablesFromSiteBins?.(site);
        global.attachHumidityLayoutSensorsToSite?.(site);
        return;
    }

    const sourceCables =
        snap.layoutCables?.length > 0
            ? snap.layoutCables
            : site.layoutCables;
    const sourceSensors =
        snap.layoutSensors?.length > 0
            ? snap.layoutSensors
            : site.layoutSensors;
    const sourceBins =
        snap.layoutBins?.length > 0 ? snap.layoutBins : site.layoutBins;

    let remapped = remapLayoutCablesToBins(
        sourceCables,
        sourceBins,
        site.layoutBins
    );
    if (remapped.length > 0) {
        remapped = translateRemappedCablesByBinDelta(
            remapped,
            sourceBins,
            site.layoutBins
        );
        remapped = scaleRemappedCablesToDraftBins(
            remapped,
            site.layoutBins,
            sourceBins
        );
        site.layoutCables = remapped;
        site.layoutSensors = remapLayoutSensorsToCables(
            sourceSensors,
            sourceCables,
            remapped
        );
    } else {
        global.populateLayoutCablesFromBins?.(site);
    }

    fillMissingLayoutCablesFromSnapshot(site, snap);
    global.fillMissingLayoutCablesFromSiteBins?.(site);
    global.attachHumidityLayoutSensorsToSite?.(site);
};

const applyStoredProxyToSiteLikePreview = (site, proxy) => {
    const fetchSnap = snapshotSiteLayout(site);
    const ok = applyLayoutTableProxyToSite(site, proxy, {
        skipCablePopulate: true,
        skipDrawSite: true,
    });
    if (!ok) return false;

    mergeLayoutBinsMissingFromSnapshot(site, fetchSnap);

    const editorDraft = loadLayoutDraftForSite3d(site.siteGuid);
    if (editorDraft) mergeRoundBinDimensionsFromDraft(site, editorDraft);

    finalizeEditorLayoutForSite3dView(site, fetchSnap);
    site.hasCommittedSite3dLayoutProxy = true;

    logLayoutProxyRoundTripDrift(site, "site3d-load", fetchSnap);
    return true;
};

/**
 * Production Site 3D — mirror mountPreviewProxyFromDraft (previewSite3d.js).
 * Uses LayoutProxyJson / layoutEditorProxy from the server; layout tables alone drift.
 * Browser cache is only used when no persisted site layout exists yet.
 */
export const applyEditorLayoutToSiteLikePreview = (site) => {
    if (!site?.siteGuid) return false;

    const layoutProxy = resolveLayoutProxyForSite3d(site);
    if (layoutProxy?.layoutBins?.length) {
        const ok = applyStoredProxyToSiteLikePreview(site, layoutProxy);
        if (ok) {
            return true;
        }
    }

    if (!site?.layoutBins?.length) return false;

    let draft = null;
    if (!site.hasPersistedSiteLayout) {
        draft = loadLayoutDraftForSite3d(site.siteGuid);
    }
    if (!draft?.groups?.length) {
        draft = buildDraftFromFetchedLayoutTables(site, {
            sidesAreGroupAbsolute: true,
        });
        if (draft) draft.sidesAreGroupAbsolute = true;
    }
    if (!draft?.groups?.length) return false;

    const fetchSnap = snapshotSiteLayout(site);
    const ok = applyLayoutDraftToSiteForPreview3d(site, draft);
    if (!ok) return false;

    mergeLayoutBinsMissingFromSnapshot(site, fetchSnap);
    mergeRoundBinDimensionsFromDraft(site, draft);
    finalizeEditorLayoutForSite3dView(site, fetchSnap);

    logLayoutProxyRoundTripDrift(site, "site3d-load", fetchSnap);
    return true;
};


